import { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bookmark } from 'lucide-react';
import { useSelector } from 'react-redux';
import useFavorites from '../../hooks/favorites/useFavorites';

const LikeButton = ({ postId, itemType }) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const { isFavorite, addToFavorites, removeFromFavorites } = useFavorites();
  const [showPopup, setShowPopup] = useState(null);

  const isCurrentlyFavorite = isFavorite(postId);

  const handleClick = async (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      setShowPopup('Login to save items');
      setTimeout(() => setShowPopup(null), 2000);
      return;
    }

    try {
      if (isCurrentlyFavorite) {
        await removeFromFavorites(postId);
        setShowPopup('Removed from saved');
      } else {
        await addToFavorites({ itemId: postId, itemType });
        setShowPopup('Saved!');
      }
    } catch (error) {
      setShowPopup('An error occurred');
    }

    setTimeout(() => setShowPopup(null), 2000);
  };

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        className={`
          p-2 rounded-full
          transition-all duration-200
          hover:scale-110 active:scale-95
          ${isCurrentlyFavorite 
            ? 'text-primary bg-primary/10' 
            : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
          }
        `}
        aria-label={isCurrentlyFavorite ? 'Remove from saved' : 'Save'}
      >
        <Bookmark 
          className={`w-5 h-5 transition-all duration-200 ${isCurrentlyFavorite ? 'fill-primary' : ''}`}
        />
      </button>

      <AnimatePresence>
        {showPopup && (
          <motion.div
            className="
              absolute top-full right-0 mt-2
              bg-foreground text-background
              px-3 py-1.5
              rounded-lg
              text-xs font-medium
              whitespace-nowrap
              shadow-lg
              z-50
            "
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
          >
            {showPopup}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default memo(LikeButton);
